import { Download, FileWarning } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { MonthPicker } from '@/components/calendar/MonthPicker';
import { FiscalStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { usePublicAccountantView } from '@/features/accountant/hooks';
import { useNfseDownload } from '@/features/nfse/hooks';
import { type FiscalStatus } from '@/features/fiscal/schemas';
import { ApiError } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PublicAccountantPage() {
  const { token } = useParams<{ token: string }>();
  const [month, setMonth] = useState<string>(currentMonth());
  const query = usePublicAccountantView(token ?? null, month);

  if (query.isError) {
    const status = (query.error as ApiError | null)?.status;
    if (status === 410) return <LinkExpiredPage />;
    if (status === 404 || status === 403) return <LinkInvalidPage />;
    return (
      <PublicLayout>
        <p role="alert" className="text-sm text-(--color-error)">
          Não foi possível carregar os dados. Tente novamente.
        </p>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-(--color-fg-muted)">
            Dashboard read-only
          </p>
          <h1 className="font-display text-2xl font-semibold">
            {query.data?.owner.name ?? 'Produção mensal'}
          </h1>
          <p className="text-sm text-(--color-fg-muted)">
            {query.data?.owner.email}
          </p>
        </div>
        <MonthPicker month={month} onChange={setMonth} />
      </header>

      {query.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : query.data ? (
        <Dashboard data={query.data} />
      ) : null}
    </PublicLayout>
  );
}

function Dashboard({
  data,
}: {
  data: NonNullable<ReturnType<typeof usePublicAccountantView>['data']>;
}) {
  const { summary, fiscalRecords } = data;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Total do mês</CardTitle>
        </CardHeader>
        <div className="flex flex-wrap items-baseline gap-4">
          <p className="text-3xl font-semibold tabular-nums">
            {formatBRL(summary.total)}
          </p>
          <p className="text-sm text-(--color-fg-muted)">
            {summary.shiftsCount} plantões · {summary.itemsCount} procedimentos
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Breakdown por serviço</CardTitle>
        </CardHeader>
        {summary.byService.length === 0 ? (
          <p className="text-sm text-(--color-fg-muted)">Sem dados no mês.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {summary.byService.map((s) => (
              <li key={s.serviceId}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden
                      className="h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="truncate text-sm font-medium">{s.name}</span>
                  </div>
                  <span className="text-sm tabular-nums">{formatBRL(s.total)}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-(--color-surface-elevated)">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(2, Math.min(100, s.percentage))}%`,
                      backgroundColor: s.color,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>NFS-e do mês</CardTitle>
        </CardHeader>
        {fiscalRecords.length === 0 ? (
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum registro fiscal neste mês.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-(--color-border)">
            {fiscalRecords.map((f) => (
              <FiscalRow key={f.record.id} view={f} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

type PublicFiscalRow = NonNullable<
  ReturnType<typeof usePublicAccountantView>['data']
>['fiscalRecords'][number];

function FiscalRow({ view }: { view: PublicFiscalRow }) {
  const { record, service } = view;
  const download = useNfseDownload();
  const toast = useToast();

  const canDownload =
    record.status === 'NFSE_EMITTED' ||
    record.status === 'NFSE_SENT' ||
    record.status === 'PAID';

  const handleDownload = async (kind: 'xml' | 'pdf'): Promise<void> => {
    try {
      // Tech spec §7.8: NFS-e signed URL é resolvida pelo endpoint /nfse/:issuanceId;
      // na view pública, o `id` do registro é usado pelo backend para resolver a
      // issuance vigente — aqui enviamos `record.id` como proxy e deixamos o backend
      // validar. Se o endpoint não estiver disponível para public, cai no catch.
      const res = await download.mutateAsync({ issuanceId: record.id, kind });
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Download indisponível';
      toast.show({ title: msg, variant: 'error' });
    }
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        {service ? (
          <span
            aria-hidden
            className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
            style={{ backgroundColor: service.color }}
          />
        ) : null}
        <div className="min-w-0">
          <p className="truncate font-medium">{service?.name ?? 'Serviço removido'}</p>
          <p className="text-xs text-(--color-fg-muted)">
            {record.reportedValue != null
              ? formatBRL(record.reportedValue)
              : formatBRL(record.computedProduction)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <FiscalStatusBadge status={record.status as FiscalStatus} />
        {canDownload ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => handleDownload('pdf')}
            aria-label="Download PDF"
          >
            <Download className="h-4 w-4" aria-hidden /> PDF
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-(--color-bg) text-(--color-fg)">
      <header className="border-b border-(--color-border) bg-(--color-surface)">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <p className="font-display text-base font-semibold">Organiza.AI</p>
          <p className="text-xs text-(--color-fg-muted)">Acesso do contador</p>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">{children}</main>
    </div>
  );
}

function LinkExpiredPage() {
  return (
    <PublicLayout>
      <Card className="flex flex-col items-start gap-3">
        <FileWarning className="h-8 w-8 text-(--color-error)" aria-hidden />
        <h1 className="font-display text-xl font-semibold">Link expirado</h1>
        <p className="text-sm text-(--color-fg-muted)">
          Este link read-only não é mais válido. Solicite um novo ao médico para continuar acessando o fechamento do mês.
        </p>
      </Card>
    </PublicLayout>
  );
}

function LinkInvalidPage() {
  return (
    <PublicLayout>
      <Card className="flex flex-col items-start gap-3">
        <FileWarning className="h-8 w-8 text-(--color-error)" aria-hidden />
        <h1 className="font-display text-xl font-semibold">Link inválido</h1>
        <p className="text-sm text-(--color-fg-muted)">
          O token informado não pertence a nenhum contador ativo. Verifique o link recebido ou peça um novo ao médico.
        </p>
      </Card>
    </PublicLayout>
  );
}
